import { faFileChartPie as ChartIcon } from '@fortawesome/pro-light-svg-icons';
import {
  faTrash as DeleteIcon,
  faDownload as DownloadIcon,
  faPen as EditIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, IconButton, ListItemText, Stack } from '@mui/material';
import {
  GridActionsCellItem,
  type GridCellParams,
  type GridColDef
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import React from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import { axiosClient } from '@/services/BaseApi.service';
import getDateTime from '@/utils/getDateTime.utils';

import type { Organization } from '../Organizations';
import type { OrganizationIdNames } from '../Organizations/Organizations.types';
import type { ReportType } from './Reports.types';

/**
 *
 * @param reportId
 * @param fileKey
 * @returns Download the attachment file
 */
export const handleDownloadClick = (reportId: string) => {
  const downloadAttachment = async () => {
    const response = await axiosClient.get(`/reports/download/${reportId}`);
    return window.open(response.data.download_url, '_blank');
  };
  return downloadAttachment();
};

const createColumns = (
  setOpenTitleEdit: (open: boolean) => void,
  deleteReport: (id: number) => void,
  reportPermissionsCheck: (permission: string) => boolean,
  setSelectedReport: (report: ReportType) => void,
  organizations: Organization[],
  selectedOrg: string,
  organizationIdNames: OrganizationIdNames
) => {
  const columns: GridColDef[] = [
    {
      field: 'icon',
      renderCell: (params) => (
        <IconButton onClick={() => handleDownloadClick(params.row.id)}>
          <FontAwesomeIcon icon={ChartIcon} />
        </IconButton>
      ),
      width: 50
    },
    {
      field: 'report_title',
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
            <ListItemText
              primary={params.value}
              secondary={getDateTime(params.row.created_at)}
            />
            {dayjs(params.row.created_at).isAfter(
              dayjs().subtract(7, 'day')
            ) && <Chip color="warning" label="New" />}
          </Stack>
        );
      }
    },
    ...(selectedOrg === 'all' && organizations
      ? [
          {
            field: 'report org',
            width: 200,
            renderCell: (params: GridCellParams) => {
              return (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {organizationIdNames[params.row.organization_id]}
                </Box>
              );
            }
          }
        ]
      : []),
    {
      type: 'actions',
      field: 'actions',
      width: 50,
      align: 'right',
      getActions: (params) => [
        reportPermissionsCheck(PERMISSIONS.REPORT_READ) ? (
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={DownloadIcon} />}
            label="Download"
            showInMenu
            onClick={() => handleDownloadClick(params.row.id)}
            key={params.row.id}
          />
        ) : (
          <React.Fragment key={params.row.id}></React.Fragment>
        ),
        reportPermissionsCheck(PERMISSIONS.REPORT_UPDATE) ? (
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={EditIcon} />}
            label="Edit Title"
            showInMenu
            onClick={() => {
              setOpenTitleEdit(true);
              setSelectedReport(params.row);
            }}
            key={params.row.id}
          />
        ) : (
          <React.Fragment key={params.row.id}></React.Fragment>
        ),
        reportPermissionsCheck(PERMISSIONS.REPORT_DELETE) ? (
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={DeleteIcon} />}
            label="Delete"
            showInMenu
            onClick={() => deleteReport(params.row.id)}
            key={params.row.id}
          />
        ) : (
          <React.Fragment key={params.row.id}></React.Fragment>
        )
      ]
    }
  ];

  return columns;
};

export default createColumns;
